import React, { useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const AdministrationPolicies = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const policyAreas = [
    {
      key: 'strategic',
      title: 'Strategic Plan',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
        </svg>
      ),
      summary: 'The Judiciary\'s 2020–2023 Strategic Plan provides direction for the federal courts.',
      content:
        'The 2020–2023 Strategic Plan for the Federal Judiciary outlines four overarching goals: deliver justice, uphold the rule of law, promote public trust and confidence, and ensure the fair administration of justice. The plan provides a roadmap for continuous improvement across all aspects of judicial administration.',
    },
    {
      key: 'rules',
      title: 'Rules and Policies',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
        </svg>
      ),
      summary: 'Federal court rules govern procedures in civil, criminal, bankruptcy, and appellate cases.',
      content:
        'The Federal Rules of Civil Procedure, Criminal Procedure, Evidence, Appellate Procedure, and Bankruptcy Procedure are promulgated under the Rules Enabling Act. The Judicial Conference, through its Standing Committee on Rules of Practice and Procedure, continuously reviews and updates these rules.',
    },
    {
      key: 'budget',
      title: 'Judiciary Budget',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
        </svg>
      ),
      summary: 'Annual budget requests, Congressional justifications, and financial management resources.',
      content:
        "The Judiciary's annual budget request to Congress reflects the resources necessary to handle the workload of the federal courts. The Administrative Office of the U.S. Courts prepares the budget and works with Congress to ensure adequate funding for the third branch of government.",
    },
    {
      key: 'records',
      title: 'Records & Archives',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 6h-2.18c.07-.44.18-.88.18-1.36 0-2.57-2.1-4.64-4.67-4.64-1.49 0-2.8.65-3.71 1.69L8 3.96 6.38 1.69C5.47.65 4.16 0 2.67 0 .1 0-2 2.07-2 4.64c0 .48.11.92.18 1.36H-2c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/>
        </svg>
      ),
      summary: 'Access court records through PACER and learn about record retention policies.',
      content:
        'The Public Access to Court Electronic Records (PACER) system allows any person with a PACER account to access federal court documents and case information. PACER provides electronic access to records from U.S. district, bankruptcy, and appellate courts.',
    },
    {
      key: 'ethics',
      title: 'Ethics & Conduct',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
        </svg>
      ),
      summary: 'Code of conduct for federal judges and court employees, ethics opinions and guidance.',
      content:
        'The Code of Conduct for United States Judges provides ethical guidance for federal judges. The Judicial Conference also provides ethics advice and educational programs on ethical issues for judges and judicial branch employees.',
    },
  ];

  const keyFacts = [
    { number: '94', label: 'Federal Judicial Districts' },
    { number: '13', label: 'Courts of Appeals Circuits' },
    { number: '179', label: 'Circuit Judgeships' },
    { number: '677', label: 'District Judgeships' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div
        className="relative py-20 px-8 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a3a5c 0%, #20476f 50%, #0057a3 100%)',
        }}
      >
        <div className="max-w-[65rem] mx-auto relative z-10">
          <p className="text-blue-200 text-sm mb-4">
            Home &rsaquo; Administration & Policies
          </p>
          <h1
            className="text-5xl font-light text-white mb-6 leading-tight"
            style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
          >
            Administration &amp; Policies
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl leading-relaxed">
            Explore the administrative framework, governing policies, financial information,
            and ethical standards that guide the federal judicial system.
          </p>
        </div>

        {/* Decorative circles */}
        <div className="absolute right-16 top-1/2 -translate-y-1/2 opacity-10 hidden md:block">
          <div className="w-64 h-64 border-2 border-white rounded-full" />
          <div className="w-48 h-48 border-2 border-white rounded-full absolute top-8 left-8" />
          <div className="w-32 h-32 border-2 border-white rounded-full absolute top-16 left-16" />
        </div>
      </div>

      {/* Key Facts Bar */}
      <div className="bg-[#20476f] text-white">
        <div className="max-w-[65rem] mx-auto px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {keyFacts.map((fact) => (
              <div key={fact.label}>
                <div className="text-3xl font-bold text-[#efd388]">{fact.number}</div>
                <div className="text-sm text-blue-200 mt-1">{fact.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[65rem] mx-auto px-8 py-16">
        <div className="flex flex-col md:flex-row gap-12">

          {/* Left: Accordion */}
          <div className="flex-1">
            <h2
              className="text-2xl font-semibold text-gray-900 mb-8"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              Policy Areas
            </h2>

            <div className="space-y-3">
              {policyAreas.map((area) => (
                <div
                  key={area.key}
                  className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#0057a3] transition-colors"
                >
                  <button
                    onClick={() =>
                      setOpenAccordion(openAccordion === area.key ? null : area.key)
                    }
                    className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-[#0057a3] flex-shrink-0">
                        {area.icon}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{area.title}</div>
                        <div className="text-sm text-gray-500 mt-0.5">{area.summary}</div>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-4 transition-transform duration-200 ${
                        openAccordion === area.key ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {openAccordion === area.key && (
                    <div className="px-6 pb-5 pt-2 bg-gray-50 border-t border-gray-100">
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">
                        {area.content}
                      </p>
                      <a
                        href="#"
                        className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline"
                      >
                        Read more
                        <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="w-full md:w-72 flex-shrink-0 space-y-6">
            {/* Judicial Conference card */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
              <h3
                className="font-semibold text-gray-900 mb-3"
                style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
              >
                Judicial Conference
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                The Judicial Conference of the United States is the national policy-making body
                for the federal courts, chaired by the Chief Justice.
              </p>
              <a
                href="#"
                className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline"
              >
                Learn about the Conference
                <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </div>

            {/* AO card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Administrative Office</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                The Administrative Office of the U.S. Courts supports the federal court system
                by providing a broad range of legislative, legal, financial, and management services.
              </p>
              <a
                href="#"
                className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline"
              >
                Visit the AO
                <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </div>

            {/* Recent updates */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Policy Updates</h3>
              <div className="space-y-4">
                {[
                  { date: 'Dec 1, 2024', title: 'Rules Amendments Effective' },
                  { date: 'Nov 15, 2024', title: 'FY2025 Budget Approved' },
                  { date: 'Oct 3, 2024', title: 'New Ethics Guidance Issued' },
                ].map((update) => (
                  <div key={update.title} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                    <div className="text-xs text-gray-400 mb-1">{update.date}</div>
                    <a href="#" className="text-sm text-[#0057a3] hover:underline font-medium">
                      {update.title}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Banner */}
      <div className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-[65rem] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Download Annual Report', desc: '2023 Director\'s Annual Report now available', btn: 'Download PDF' },
              { title: 'Submit a Comment', desc: 'Public comment periods for proposed rule changes', btn: 'View Open Comments' },
              { title: 'Contact the AO', desc: 'Reach out to the Administrative Office directly', btn: 'Get in Touch' },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-4 flex-grow">{item.desc}</p>
                <button className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline">
                  {item.btn} <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministrationPolicies;